import { Box, Checkbox, Menu, MenuItem } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { ChevronDown, ChevronUp, Close } from '@sweep-io/sweep-design/dist/icons';
import React, { useMemo, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectConnectedCrmOrgs, selectDefaultCreationEnvironment } from '../../pages/environments/environmentsReducer';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { selectSelectedAdditionalOrgIds, setSelectedAdditionalOrgIds, clearSelectedAdditionalOrgIds, setAdvancedMode } from '../agentsHomeReducer';
import { AgentInputMode } from './agentInputModeTypes';
import { CrmOrg } from '../../../types/CrmOrgs';
import { MultiOrgIcon } from './icons';

const getOrgNamesText = (selectedOrgIds: string[], availableOrgs: CrmOrg[]) => {
  if (selectedOrgIds.length === 0) {
    return 'Select Environments';
  }

  const selectedOrgs = availableOrgs.filter((org) => selectedOrgIds.includes(org.id));
  const names = selectedOrgs.map((org) => org.name);

  if (names.length <= 2) {
    return names.join(', ');
  }

  // Show first 2 names, then +N count
  return `${names[0]}, ${names[1]}, +${names.length - 2}`;
};

const MultiOrgFancyTagSelector = ({ colorVariant }: { colorVariant: Record<number, string> }) => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const pillRef = useRef<HTMLDivElement>(null);

  // Get all connected orgs and filter out current org
  const connectedOrgs = useSelector(selectConnectedCrmOrgs);
  const currentOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const currentOrg = useSelector(selectDefaultCreationEnvironment);
  const selectedOrgIds = useSelector(selectSelectedAdditionalOrgIds);

  const availableOrgs = useMemo(
    () => connectedOrgs.filter((org) => org.id !== currentOrgId),
    [connectedOrgs, currentOrgId],
  );

  const handlePillClick = () => {
    setAnchorEl(pillRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOrgToggle = (orgId: string) => {
    const newSelection = selectedOrgIds.includes(orgId)
      ? selectedOrgIds.filter((id) => id !== orgId)
      : [...selectedOrgIds, orgId];
    dispatch(setSelectedAdditionalOrgIds({ orgIds: newSelection }));
  };

  const handleClearAll = () => {
    dispatch(clearSelectedAdditionalOrgIds());
  };

  const handleDeletePill = (event: React.MouseEvent) => {
    event.stopPropagation();
    // Disable multi-org mode entirely
    dispatch(setAdvancedMode({ advancedMode: AgentInputMode.None }));
  };

  const orgNamesText = getOrgNamesText(selectedOrgIds, availableOrgs);
  const pillColor = colorVariant;

  return (
    <Box display="flex" alignItems="center">
      {/* Pill/Tag */}
      <Box
        ref={pillRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          backgroundColor: pillColor[100],
          border: `1px solid ${pillColor[700]}`,
          borderRadius: '8px',
          minHeight: '32px',
          padding: '0 8px',
          cursor: 'default',
          '&:hover': {
            opacity: 0.9,
          },
        }}
      >
        <MultiOrgIcon size={16} color={pillColor[700]} />
        <Typography variant="body" color={pillColor[700]}>
          Multi-Org
        </Typography>
        <Box
          onClick={handlePillClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            backgroundColor: 'white',
            border: `1px solid ${colors.grey[300]}`,
            borderRadius: '4px',
            padding: '2px 6px',
            minHeight: '24px',
            cursor: 'pointer',
            flex: 1,
            maxWidth: '300px',
          }}
        >
          <Typography variant="caption" color={colors.grey[700]}>
            {orgNamesText}
          </Typography>
          {open ? (
            <ChevronUp size="small" color={colors.grey[700]} />
          ) : (
            <ChevronDown size="small" color={colors.grey[700]} />
          )}
        </Box>
        <Box
          onClick={handleDeletePill}
          sx={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: '4px',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.7,
            },
          }}
        >
          <Close size="small" color={pillColor[700]} />
        </Box>
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              minWidth: '280px',
              maxHeight: '400px',
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 1 }}>
          <Box
            sx={{
              '& .MuiTypography-root': {
                textTransform: 'uppercase',
              },
            }}
          >
          </Box>
          <Typography variant="caption" textTransform="uppercase" color={colors.grey[700]}>
            Compare with {currentOrg?.name || 'current org'}
          </Typography>
        </Box>

        {/* Org List */}
        {availableOrgs.map((org) => (
          <MenuItem
            key={org.id}
            onClick={() => handleOrgToggle(org.id)}
          >
            <Box display="flex" alignItems="center">
              <Checkbox
                checked={selectedOrgIds.includes(org.id)}
              />
              <Box display="flex" flexDirection="column">
                <Typography variant="body">{org.name}</Typography>
                <Typography variant="caption" color={colors.grey[400]}>
                  {org.isSandbox ? 'Sandbox' : 'Production'}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}

        {/* Clear all footer */}
        {selectedOrgIds.length > 0 && (
          <MenuItem
            onClick={handleClearAll}
            sx={{
              borderTop: `1px solid ${colors.grey[200]}`,
            }}
          >
            <Typography variant="body" color={colors.grey[700]}>
              Clear all
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default MultiOrgFancyTagSelector;

